/**
 * Skeleton primitives — the grey shimmering blocks every loading layout in
 * Creator Studio is built from.
 *
 * `Skeleton` is one block; `SkeletonText` is a stack of them at line height.
 * Both are purely presentational (`aria-hidden`): a skeleton stands in for
 * content that is still on its way and says nothing on its own. Styles live
 * in app/styles/creator-skeleton.css (`.cs-skel`), linked from root.tsx so
 * they are present on every page. Nothing here reads the viewport.
 */
import type { CSSProperties } from 'react';

export interface SkeletonProps {
  /** CSS length; a number is px. Defaults to the full width of the parent. */
  width?: number | string;
  /** CSS length; a number is px. Defaults to one text line. */
  height?: number | string;
  /** CSS length; a number is px. 8px by default, `'50%'` for a circle. */
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({ width = '100%', height = 14, radius = 8, className, style }: SkeletonProps) {
  return (
    <span
      className={className ? `cs-skel ${className}` : 'cs-skel'}
      style={{ width, height, borderRadius: radius, ...style }}
      aria-hidden="true" />);


}

export interface SkeletonTextProps {
  /** Number of lines; the last one is drawn shorter so the block reads as prose. */
  lines?: number;
  /** Line height in px. */
  lineHeight?: number;
  /** Per-line widths, overriding the default full/full/…/60% pattern. */
  widths?: Array<number | string>;
  className?: string;
}

export function SkeletonText({ lines = 3, lineHeight = 12, widths, className }: SkeletonTextProps) {
  const count = Math.max(1, lines);
  return (
    <div className={className ? `cs-skel-text ${className}` : 'cs-skel-text'} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => {
        const fallback = count > 1 && i === count - 1 ? '60%' : '100%';
        return <Skeleton key={i} width={widths?.[i] ?? fallback} height={lineHeight} radius={6} />;
      })}
    </div>);

}